import type React from "react";
import { useEffect, useState } from "react";

import type { TicketFiltersDto } from "@/api/types";
import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { RepairRequestFilterTypes } from "@/queries/tickets/constants";
import { reporterTypeValues, residentTicketTypeValues } from "@/queries/tickets/constants";
import { useTranslation } from "@/translations";

export type ResidentTicketsFilterValues = {
  statusIds?: string[];
  categoryIds?: string[];
  reporterIds?: string[];
  visibility?: RepairRequestFilterTypes;
};

type ResidentTicketsFilterDrawerProps = DrawerBaseProps & {
  filter: ResidentTicketsFilterValues;
  filterOptions: TicketFiltersDto;
  onUpdate: (filter: ResidentTicketsFilterValues) => void;
};

export function ResidentTicketsFilterDrawer({
  filter,
  onUpdate,
  filterOptions,
  isOpened,
  onOpenChange,
}: ResidentTicketsFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<ResidentTicketsFilterValues>(filter);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(filter);
  }, [filter]);

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    onUpdate({
      statusIds: undefined,
      categoryIds: undefined,
      reporterIds: undefined,
      visibility: "both",
    });
  };

  return (
    <Drawer.Root title={t("page.resident-tickets.filter-drawer.title")} onOpenChange={onOpenChange} isOpened={isOpened}>
      <Drawer.Body>
        <Stack gap="md">
          <FormField label={t("page.resident-tickets.filter-drawer.status.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={filterOptions.statuses}
              selected={filterOptions.statuses.filter((x) => currFilter.statusIds?.includes(x.id))}
              onChange={(s) => setCurrFilter({ ...currFilter, statusIds: s.map(({ id }) => id) })}
              placeholder={t("page.resident-tickets.filter-drawer.status.placeholder")}
              renderOption={(x) => <TicketStatus labelColor={x.color} description={x.name} variant="filled" />}
              keySelector={(x) => x.id}
            />
          </FormField>
          <FormField label={t("page.resident-tickets.filter-drawer.category.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={filterOptions.categories}
              selected={filterOptions.categories.filter((x) => currFilter.categoryIds?.includes(x.id))}
              onChange={(c) => setCurrFilter({ ...currFilter, categoryIds: c.map(({ id }) => id) })}
              placeholder={t("page.resident-tickets.filter-drawer.category.placeholder")}
              renderOption={(x) => x.name}
              keySelector={(x) => x.id}
            />
          </FormField>
          <FormField label={t("page.resident-tickets.filter-drawer.reporter.label")}>
            <Select
              items={reporterTypeValues}
              selected={currFilter.reporterIds?.length ? "mine" : "all"}
              onChange={(x) => setCurrFilter({ ...currFilter, reporterIds: x === "all" ? [] : [sessionUser.id] })}
              renderOption={(x) => {
                switch (x) {
                  case "mine":
                    return t("page.resident-tickets.filter.reporter.mine");
                  case "all":
                    return t("page.resident-tickets.filter.reporter.all");
                }
              }}
              keySelector={(x) => x}
            />
          </FormField>
          <FormField label={t("page.resident-tickets.filter-drawer.visibility.label")}>
            <Select
              items={residentTicketTypeValues}
              selected={currFilter.visibility}
              onChange={(x) => setCurrFilter({ ...currFilter, visibility: x })}
              renderOption={(x) => {
                switch (x) {
                  case "both":
                    return t("common.label.ticket-visibility.all");
                  case "onlyPrivate":
                    return t("common.label.ticket-visibility.private");
                  case "onlyCollective":
                    return t("common.label.ticket-visibility.collective");
                }
              }}
              keySelector={(x) => x}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
